import React from 'react';

export type ErrorType = 'network' | 'auth' | 'server' | 'validation' | 'not-found' | 'generic';

interface ErrorDisplayProps {
  type?: ErrorType;
  title?: string;
  message?: string;
  onRetry?: () => void;
  onGoBack?: () => void;
  showRetry?: boolean;
  showGoBack?: boolean;
  className?: string;
}

const errorConfig: Record<ErrorType, {
  icon: string;
  iconColor: string;
  defaultTitle: string;
  defaultMessage: string;
}> = {
  network: {
    icon: 'wifi_off',
    iconColor: 'text-orange-500',
    defaultTitle: 'Connection Problem',
    defaultMessage: 'Unable to connect to the server. Please check your internet connection and try again.',
  },
  auth: {
    icon: 'lock',
    iconColor: 'text-red-500',
    defaultTitle: 'Authentication Required',
    defaultMessage: 'Your session has expired. Please log in again to continue.',
  },
  server: {
    icon: 'error',
    iconColor: 'text-red-500',
    defaultTitle: 'Server Error',
    defaultMessage: 'Something went wrong on our end. We\'re working to fix it. Please try again in a moment.',
  },
  validation: {
    icon: 'warning',
    iconColor: 'text-amber-500',
    defaultTitle: 'Invalid Input',
    defaultMessage: 'Please check your input and try again.',
  },
  'not-found': {
    icon: 'search_off',
    iconColor: 'text-gray-500',
    defaultTitle: 'Not Found',
    defaultMessage: 'The content you\'re looking for doesn\'t exist or has been moved.',
  },
  generic: {
    icon: 'error',
    iconColor: 'text-gray-500',
    defaultTitle: 'Something Went Wrong',
    defaultMessage: 'An unexpected error occurred. Please try again.',
  },
};

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  type = 'generic',
  title,
  message,
  onRetry,
  onGoBack,
  showRetry = true,
  showGoBack = false,
  className = '',
}) => {
  const config = errorConfig[type];
  const displayTitle = title || config.defaultTitle;
  const displayMessage = message || config.defaultMessage;

  return (
    <div className={`bg-red-50 border border-red-200 rounded-lg p-6 sm:p-8 text-center ${className}`}>
      <span className={`material-symbols-outlined ${config.iconColor} text-4xl sm:text-5xl mb-3 block`}>
        {config.icon}
      </span>
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
        {displayTitle}
      </h2>
      <p className="text-sm sm:text-base text-gray-600 mb-4 max-w-md mx-auto">
        {displayMessage}
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
        {showRetry && onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-primary-500 text-white text-sm sm:text-base font-medium rounded-lg hover:bg-primary-600 transition-colors w-full sm:w-auto"
          >
            <span className="material-symbols-outlined text-lg sm:text-xl mr-2">
              refresh
            </span>
            Try Again
          </button>
        )}
        
        {showGoBack && onGoBack && (
          <button
            onClick={onGoBack}
            className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 text-sm sm:text-base font-medium rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto"
          >
            <span className="material-symbols-outlined text-lg sm:text-xl mr-2">
              arrow_back
            </span>
            Go Back
          </button>
        )}
      </div>

      {type === 'server' && (
        <p className="mt-4 text-xs text-gray-500">
          If this problem persists, please contact support.
        </p>
      )}
    </div>
  );
};

